import { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaKey, FaCopy, FaUsers } from 'react-icons/fa';
import futbolistaService from '../../services/futbolistaService';
import TutorLayout from '../../components/tutor/TutorLayout';

const FutbolistaCredenciales = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [credencialesData, setCredencialesData] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });

  useEffect(() => {
    loadCredenciales();
  }, [id]);

  const loadCredenciales = async () => {
    try {
      setLoading(true);
      const response = await futbolistaService.getCredenciales(id);
      setCredencialesData(response.data);
    } catch (error) {
      console.error('Error al obtener credenciales:', error);
      setAlert({
        show: true,
        message: 'Error al obtener las credenciales del futbolista',
        variant: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setAlert({
        show: true,
        message: 'Copiado al portapapeles',
        variant: 'success'
      });
      // Auto-hide alert after 2 seconds
      setTimeout(() => {
        setAlert({ show: false, message: '', variant: 'success' });
      }, 2000);
    }).catch(() => {
      setAlert({
        show: true,
        message: 'Error al copiar al portapapeles',
        variant: 'danger'
      });
    });
  };

  if (loading) {
    return (
      <TutorLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      </TutorLayout>
    );
  }

  if (!credencialesData) {
    return (
      <TutorLayout>
        <Alert variant="danger">
          <h4>Credenciales no encontradas</h4>
          <p>No se pudieron obtener las credenciales del futbolista.</p>
          <Button variant="outline-danger" onClick={() => navigate('/tutor/futbolistas')}>
            Volver a Mis Futbolistas
          </Button>
        </Alert>
      </TutorLayout>
    );
  }

  return (
    <TutorLayout>
      {/* Header con navegación */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button 
            variant="outline-secondary" 
            onClick={() => navigate('/tutor/futbolistas')}
            className="mb-2"
            size="sm"
          >
            <FaArrowLeft className="me-2" />
            Volver a Mis Futbolistas
          </Button>
          <h2 className="text-primary mb-1">
            <FaKey className="me-2" />
            Credenciales de Acceso
          </h2>
          <p className="text-muted mb-0">Datos de acceso para la aplicación móvil</p>
        </div>
      </div>

      {alert.show && (
        <Alert variant={alert.variant} onClose={() => setAlert({ show: false, message: '', variant: 'success' })} dismissible className="mb-4">
          {alert.message}
        </Alert>
      )}

      {/* Información del Futbolista */}
      <Card className="mb-4">
        <Card.Header className="bg-primary text-white">
          <h5 className="mb-0">
            <FaUsers className="me-2" />
            {credencialesData.futbolista.nombre}
          </h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <p className="mb-1">
                <strong>Fecha de Nacimiento:</strong> {credencialesData.futbolista.fecha_nacimiento || '-'}
              </p>
            </Col>
            <Col md={6}>
              <p className="mb-1">
                <strong>Apodo:</strong> {credencialesData.futbolista.apodo || '-'}
              </p>
            </Col>
          </Row>
          {credencialesData.credenciales.mensaje && (
            <Alert variant="info" className="mt-3 mb-0">
              {credencialesData.credenciales.mensaje}
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Credenciales */}
      <Card className="mb-4">
        <Card.Header>
          <h5 className="mb-0">Datos de Acceso</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6} className="mb-3">
              <label className="form-label fw-bold">Email de acceso:</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={credencialesData.credenciales.email || ''}
                  readOnly
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => handleCopyToClipboard(credencialesData.credenciales.email)}
                  title="Copiar email"
                >
                  <FaCopy />
                </button>
              </div>
            </Col>

            <Col md={6} className="mb-3">
              <label className="form-label fw-bold">Contraseña:</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={credencialesData.credenciales.contraseña || ''}
                  readOnly
                />
                <button
                  className="btn btn-outline-secondary"
                  type="button"
                  onClick={() => handleCopyToClipboard(credencialesData.credenciales.contraseña)}
                  title="Copiar contraseña"
                >
                  <FaCopy />
                </button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Instrucciones */}
      <Alert variant="warning">
        <h6 className="alert-heading">⚠️ Importante:</h6>
        <ul className="mb-0">
          <li>Guarda estas credenciales en un lugar seguro</li>
          <li>El futbolista las necesitará para acceder a la aplicación móvil</li>
          <li>No compartas estas credenciales con terceros</li>
          <li>Si olvidas la contraseña, contacta con el administrador</li>
        </ul>
      </Alert>

      {/* Botones de acción */}
      <div className="d-flex gap-2">
        <Button 
          variant="primary"
          onClick={() => navigate(`/tutor/futbolistas/${id}`)}
        >
          Ver Detalles del Futbolista
        </Button>
        <Button 
          variant="outline-secondary"
          onClick={() => navigate('/tutor/futbolistas')}
        >
          Volver a la Lista
        </Button>
      </div>
    </TutorLayout>
  );
};

export default FutbolistaCredenciales;
